<template>
  <v-container fluid>
    <v-row>
      <v-col cols="12">
        <v-card>
          <v-card-title class="primary white--text">
            <v-icon left color="white">mdi-finance</v-icon>
            Reflective Log Questions Management
          </v-card-title>

          <v-card-text>
            <v-form @submit.prevent="submitQuestions">
              <v-row class="mb-1">
                <v-col cols="12" class="mt-3 pb-1">
                  <h3 class="section-subtitle">Add Reflective Questions</h3>
                </v-col>
              </v-row>

              <v-row no-gutters>
                <v-col
                  v-for="(question, index) in form.questions"
                  :key="`question-${index}`"
                  cols="12"
                  class="question-row"
                >
                  <div class="d-flex align-start">
                    <v-textarea
                      v-model.trim="question.text"
                      outlined
                      dense
                      auto-grow
                      rows="2"
                      no-resize
                      class="question-input"
                      hide-details="auto"
                      :counter="200"
                      :label="`Question ${index + 1}:`"
                      :error-messages="questionErrors(index)"
                      @input="touchQuestion(index)"
                      @blur="touchQuestion(index)"
                    ></v-textarea>
                    <v-btn
                      v-if="form.questions.length > 1"
                      icon
                      color="red"
                      class="ml-2 mt-1"
                      @click="removeQuestionInput(index)"
                    >
                      <v-icon>mdi-minus-circle-outline</v-icon>
                    </v-btn>
                  </div>
                </v-col>
              </v-row>

              <v-row>
                <v-col cols="12" class="d-flex flex-wrap gap-2">
                  <v-btn color="primary" @click="addQuestionInput">
                    <v-icon left>mdi-plus</v-icon>
                    Add Question
                  </v-btn>
                  <v-btn color="success" type="submit">
                    <v-icon left>mdi-content-save</v-icon>
                    Submit Questions
                  </v-btn>
                </v-col>
              </v-row>
            </v-form>

            <v-divider class="my-6"></v-divider>

            <v-sheet outlined>
              <v-data-table
                :headers="tableHeaders"
                :items="getQuestions"
                item-key="rec_id"
                class="elevation-1"
              >
                <template v-slot:top>
                  <v-toolbar flat>
                    <v-toolbar-title
                      >Available Reflective Questions</v-toolbar-title
                    >
                    <v-spacer></v-spacer>
                    <v-btn small color="primary" @click="loadRefLogQuestions()">
                      <v-icon left small>mdi-refresh</v-icon>
                      Reload
                    </v-btn>
                  </v-toolbar>
                </template>

                <template v-slot:[`item.state`]="{ item }">
                  <v-chip
                    small
                    :color="item.state === 1 ? 'green' : 'orange'"
                    dark
                  >
                    {{ item.state === 1 ? "Active" : "Removed" }}
                  </v-chip>
                </template>
                <template v-slot:[`item.created_on`]="{ item }">
                  {{ item.created_on | dateReOrderRMtime }}
                </template>
                <template v-slot:[`item.updated_on`]="{ item }">
                  {{ item.updated_on | dateReOrderRMtime }}
                </template>

                <template v-slot:[`item.actions`]="{ item }">
                  <v-btn
                    v-if="item.state === 1"
                    dark
                    small
                    color="red"
                    @click="openRemoveDialog(item)"
                  >
                    <v-icon left small>mdi-trash-can-outline</v-icon>
                    Remove
                  </v-btn>
                  <v-chip v-else small color="grey" dark>
                    Already removed
                  </v-chip>
                </template>
              </v-data-table>
            </v-sheet>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <v-dialog v-model="dialogRemove" max-width="600px">
      <v-card>
        <v-card-title class="text-h5">
          Remove this question from future forms?
        </v-card-title>
        <v-card-text>
          {{ selectedQuestion.question }}
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="blue darken-1" text @click="closeRemoveDialog"
            >Cancel</v-btn
          >
          <v-btn color="blue darken-1" text @click="confirmRemoveQuestion"
            >OK</v-btn
          >
          <v-spacer></v-spacer>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import { required, maxLength, minLength } from "vuelidate/lib/validators";
import { mapActions, mapGetters } from "vuex";
import {
  M_OU,
  GET_REFLECTIVE_LOG_QUESTIONS,
  SAVE_NEW_REFLECTIVE_LOG_QUESTION,
  LOAD_REFLECTIVE_LOG_QUESTIONS,
  DELETE_REFLECTIVE_LOG_QUESTION,
} from "@/store/mutation-list";

export default {
  name: "ReflectiveLogQuestions",

  validations: {
    form: {
      questions: {
        required,
        minLength: minLength(1),
        $each: {
          text: {
            required,
            maxLength: maxLength(200),
          },
        },
      },
    },
  },

  data() {
    return {
      form: {
        questions: [{ text: "" }],
      },
      tableHeaders: [
        { text: "ID", value: "rec_id" },
        { text: "Question", value: "question" },
        // { text: "Status", value: "state" },
        { text: "Added By", value: "added_staff" },
        { text: "Created On", value: "created_on" },
        // { text: "Updated On", value: "updated_on" },
        { text: "Actions", value: "actions", sortable: false },
      ],
      serverQuestions: [],
      dialogRemove: false,
      selectedQuestion: {},
    };
  },

  methods: {
    ...mapActions({
      saveRefLogQuestions: `${M_OU}${SAVE_NEW_REFLECTIVE_LOG_QUESTION}`,
      loadRefLogQuestions: `${M_OU}${LOAD_REFLECTIVE_LOG_QUESTIONS}`,
      deleteRefLogQuestion: `${M_OU}${DELETE_REFLECTIVE_LOG_QUESTION}`,
    }),
    addQuestionInput() {
      this.form.questions.push({ text: "" });
    },

    removeQuestionInput(index) {
      this.form.questions.splice(index, 1);
      this.$v.form.questions.$touch();
    },

    touchQuestion(index) {
      if (
        this.$v.form.questions.$each[index] &&
        this.$v.form.questions.$each[index].text
      ) {
        this.$v.form.questions.$each[index].text.$touch();
      }
    },

    questionErrors(index) {
      const errors = [];
      const currentValidation = this.$v.form.questions.$each[index];
      if (!currentValidation || !currentValidation.text.$dirty) {
        return errors;
      }

      if (!currentValidation.text.required) {
        errors.push("Question is required.");
      }
      if (!currentValidation.text.maxLength) {
        errors.push("Question cannot be more than 200 characters.");
      }
      return errors;
    },

    showAlert(type, title, message) {
      this.$fire({
        title,
        text: message,
        type,
        timer: 3000,
      }).then((r) => {
        console.log(r.value);
      });
    },

    submitQuestions() {
      this.$v.$touch();
      if (this.$v.$invalid) {
        this.showAlert(
          "error",
          "Validation Error",
          "Please fix all validation errors before submitting questions."
        );
        return;
      }
      let retPayload = [];
      this.form.questions.forEach((item) => {
        retPayload.push({ question: item.text });
      });

      this.saveRefLogQuestions({
        questions: JSON.stringify(retPayload),
      }).then((response) => {
        if (response === "SUCCESS") {
          this.showAlert("success", "Success", "Questions added successfuly.");
          this.form.questions = [{ text: "" }];
          this.$v.$reset();
          this.loadRefLogQuestions();
          return;
        }

        this.showAlert("error", "Error", "Failed to save questions.");
      });
    },

    openRemoveDialog(item) {
      this.selectedQuestion = { ...item };
      this.dialogRemove = true;
    },

    closeRemoveDialog() {
      this.dialogRemove = false;
      this.selectedQuestion = {};
    },

    confirmRemoveQuestion() {
      this.deleteRefLogQuestion({ id: this.selectedQuestion.rec_id }).then(
        (response) => {
          if (response === "SUCCESS") {
            const selectedId = this.selectedQuestion.rec_id;
            this.serverQuestions = this.serverQuestions.map((question) => {
              if (question.rec_id === selectedId) {
                return {
                  ...question,
                  state: 0,
                  updated_on: response.updated_on,
                };
              }
              return question;
            });
            this.showAlert("success", "Success", response.message);
            this.loadRefLogQuestions();
          } else {
            this.showAlert("error", "Error", response.message);
          }

          this.closeRemoveDialog();
        }
      );
    },
  },
  computed: {
    ...mapGetters({
      getQuestions: `${M_OU}${GET_REFLECTIVE_LOG_QUESTIONS}`,
    }),
  },

  created() {
    this.loadRefLogQuestions();
  },
};
</script>

<style scoped>
.v-card-title {
  font-weight: 600;
}

.section-subtitle {
  margin: 4px 0 0;
  font-size: 18px;
  font-weight: 600;
  color: #1f2937;
}

.question-row {
  margin-bottom: 4px;
}

.question-input :deep(textarea) {
  word-break: break-word;
}

.gap-2 {
  gap: 8px;
}
</style>
